/**
 * "Catch A": the rules, shared by the browser (which plays them) and the
 * server (which replays them to check a win). Everything is derived from one
 * seed, so both sides compute the exact same sequence of hops.
 *
 * Positions are fractions of the browser window (0..1): A hops across the page
 * itself. Distances are measured in units of the window's shorter side, so
 * the hit box is the same on any screen.
 * Tune difficulty here and nowhere else.
 */
export const GAME = {
  /** play time, ms */
  duration: 10_000,
  /** catches needed to win */
  need: 7,
  /** delay before the first hop, ms */
  lead: 500,
  /** how long A stays on a spot: first hop → last hop, ms */
  showFirst: 700,
  showLast: 380,
  /** pause between hops, ms */
  gapMin: 110,
  gapMax: 260,
  /** every Nth spot may be a grey decoy; catching one costs a point */
  decoyEvery: 3,
  /** half-size of A's hit box, in short-side units */
  hitW: 0.04,
  hitH: 0.056,
  /** faster than this after A lands is not a human, ms */
  minReaction: 140,
  /** tries per visitor per day (soft limit, kept in the browser) */
  triesPerDay: 3,
} as const;

export type Spot = { t0: number; t1: number; x: number; y: number; decoy: boolean };
export type Click = { t: number; x: number; y: number };

/** Small, fast, deterministic PRNG (mulberry32). */
function rng(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Where and when A appears, for a given seed. */
export function schedule(seed: number): Spot[] {
  const r = rng(seed);
  const spots: Spot[] = [];
  let t: number = GAME.lead;
  let px = 0.5, py = 0.5;
  for (let i = 0; t < GAME.duration; i++) {
    const p = Math.min(1, i / 11);
    const show = Math.round((GAME.showFirst + (GAME.showLast - GAME.showFirst) * p) * (0.9 + r() * 0.2));
    // the next spot always lands well away from the last one
    let x = 0.5, y = 0.5;
    for (let k = 0; k < 12; k++) {
      x = 0.1 + r() * 0.8;
      y = 0.14 + r() * 0.72;
      if (Math.hypot(x - px, y - py) > 0.32) break;
    }
    px = x; py = y;
    const decoy = i % GAME.decoyEvery === GAME.decoyEvery - 1 && r() < 0.85;
    const t1 = Math.min(GAME.duration, t + show);
    spots.push({ t0: t, t1, x, y, decoy });
    t = t1 + GAME.gapMin + Math.round(r() * (GAME.gapMax - GAME.gapMin));
  }
  return spots;
}

/**
 * Replay the clicks against the schedule. `aspect` = play area width / height.
 * Each real spot counts once; a click on a decoy costs a point.
 */
export function score(seed: number, clicks: Click[], aspect: number): { hits: number; win: boolean; suspicious: boolean } {
  const spots = schedule(seed);
  const a = Math.min(3, Math.max(0.33, aspect || 1));
  // fractions → short-side units
  const sx = a >= 1 ? a : 1, sy = a >= 1 ? 1 : 1 / a;
  const caught = new Set<number>();
  let hits = 0;
  let suspicious = false;
  let last = -1;
  // how each real catch was made: offset from A's centre (0..1 of the hit box) and reaction time
  const offs: number[] = [];
  const reacts: number[] = [];
  // a hand needs time to cross the screen (Fitts's law): catches faster than a
  // generous human floor for that distance are machine clicks
  let px = 0.5, py = 0.5, tooFast = 0;
  for (const c of clicks.slice(0, 80)) {
    if (!(c.t >= 0 && c.t <= GAME.duration + 50) || c.t < last) { suspicious = true; continue; }
    last = c.t;
    const i = spots.findIndex((s) => c.t >= s.t0 && c.t <= s.t1);
    if (i < 0 || caught.has(i)) continue;
    const s = spots[i];
    const dx = Math.abs(c.x - s.x) * sx, dy = Math.abs(c.y - s.y) * sy;
    if (dx > GAME.hitW || dy > GAME.hitH) continue;
    if (c.t - s.t0 < GAME.minReaction) { suspicious = true; continue; }
    caught.add(i);
    hits = s.decoy ? Math.max(0, hits - 1) : hits + 1;
    if (!s.decoy) {
      offs.push(Math.max(dx / GAME.hitW, dy / GAME.hitH));
      reacts.push(c.t - s.t0);
      const dist = Math.hypot((c.x - px) * sx, (c.y - py) * sy);
      if (c.t - s.t0 < 140 + 75 * Math.log2(1 + dist / 0.08)) tooFast++;
      px = c.x;
      py = c.y;
    }
  }
  if (tooFast >= 2) suspicious = true;
  // humans scatter around the target and never react with machine-even timing
  if (offs.length >= GAME.need) {
    const meanOff = offs.reduce((a, b) => a + b, 0) / offs.length;
    const mean = reacts.reduce((a, b) => a + b, 0) / reacts.length;
    const sd = Math.sqrt(reacts.reduce((a, b) => a + (b - mean) ** 2, 0) / reacts.length);
    if (meanOff < 0.06 || sd < 12) suspicious = true;
  }
  return { hits, win: !suspicious && hits >= GAME.need, suspicious };
}

/** A game id people can read out: the seed in base 36, upper case. */
export const gameId = (seed: number) => seed.toString(36).toUpperCase();
